import {
  CustomDataGrid,
  MAutocomplete,
  MButton,
  MCheckbox,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { IsotopeResultCalculationResponseTypes } from "@app/types/samples.types";
import { Box, Grid, Modal, Typography } from "@mui/material";
import {
  useEquipment,
  useIsotoposInstalacion,
} from "@services/servicesGenericsTables";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  columnsLidCharterizacionFalse,
  columnsOnlyCharterizacion,
  columnsOnlyLidORBothTrue,
  columnsWhitMatch,
  columnsWhitOutMatch,
  styleModal,
} from "./helpers";
import { useSample } from "@app/pages/samples/utils/useSample";
import { useParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { Document, Page, Text, View, StyleSheet, pdf } from "@react-pdf/renderer";
import { toExponentialUppercaseWhitZero } from "@app/utils/functions";
import {
  PDFHeader,
  PDFTitle,
  SampleBasicInfo,
  PreparationInfo,
  styles as pdfComponentStyles,
} from "@app/pages/samples/utils/pdf/PDFComponents";
import dayjs from "dayjs";

const OutData = () => {
  const { getValues, setValue, watch } = useFormContext();
  const typeReport = getValues("calculation_type_report");
  const {
    fetchIsotopeResultCalculationByID,
    currentIsotopeResultCalculationResponseTypes,
    putResult,
    fetchCalculation,
  } = useAnalysis();
  const [rowsInput, setRowsInput] = useState<
    IsotopeResultCalculationResponseTypes[]
  >([]);
  const { idMuestra } = useParams();
  const [open, setOpen] = useState({ state: false, type: "" });
  const modalContentRef = useRef<HTMLDivElement>(null);
  const typeAnalysisName = getValues("typeAnalysis")?.descripcion;
  const { equipmentList } = useEquipment();
  const { isotoposInstalacionList } = useIsotoposInstalacion();

  const { get } = useSample();

  // Estilos personalizados con fuentes incrementadas un 25% sobre la reducción del 50%
  const pdfStyles = StyleSheet.create({
    page: {
      ...pdfComponentStyles.page,
      fontSize: 6.9,
    },
    table: {
      ...pdfComponentStyles.table,
    },
    tableRow: {
      ...pdfComponentStyles.tableRow,
    },
    tableCell: {
      ...pdfComponentStyles.tableCell,
      fontSize: 6.3,
      padding: 2.5,
    },
    tableCellLabel: {
      ...pdfComponentStyles.tableCellLabel,
      fontSize: 6.3,
      padding: 2.5,
    },
  });

  useEffect(() => {
    const preloadResults = async () => {
      const idResultPreload = getValues("idResultPreload");
      if (idResultPreload !== undefined) {
        const response = await fetchCalculation(idResultPreload);
        if (response) {
          setValue("to_report", response.to_reports ? "to_report" : "");
        }
      }
    };

    preloadResults();
  }, [getValues("idResultPreload")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToReportUpdate = () => {
    if (getValues("idResultPreload")) {
      putResult(getValues("idResultPreload"), true);
      setValue("to_report", "to_report");
    }
  };

  const memoFilterIsotoposInstalacion: any = useMemo(() => {
    const sample = get(Number(idMuestra));
    const filterIsotoposInstalacion =
      isotoposInstalacionList
        .filter(
          (iso) => iso.instalacion === sample?.sampling_points?.instalacion
        )
        ?.map((iso) => iso.isotopo) || [];

    const normalizedFilterIsotoposInstalacion = filterIsotoposInstalacion.map(
      (iso) => iso.replace(/-/g, '').toUpperCase()
    );
    
    const isotopesFilterWithMatch = rowsInput.filter((row) =>
      normalizedFilterIsotoposInstalacion.includes(
      row.isotope.codigo.replace(/-/g, '').toUpperCase()
      )
    );
    return isotopesFilterWithMatch;
  }, [idMuestra, isotoposInstalacionList, rowsInput]); // eslint-disable-line

  const handleClose = () => {
    setOpen({ state: false, type: "" });
  };

  useEffect(() => {
    setRowsInput(currentIsotopeResultCalculationResponseTypes);
  }, [currentIsotopeResultCalculationResponseTypes]);

  const handleOpen = async (type) => {
    setOpen({ state: true, type: type });
    await fetchIsotopeResultCalculationByID();
  };

  const generatePDFDocument = (rows: IsotopeResultCalculationResponseTypes[], columns: GridColDef[], title: string) => {
    const currentDate = new Date().toLocaleDateString();
    const sample = get(Number(idMuestra));
    const sourceApp = getValues("source_app_calculation_info") || getValues("source_app_calculation_info_previus");
    const analysisTypeCode = sourceApp === "GENIE" ? "IG-GENIE" : "IG-EXE";

    const dataObjects = {
      ...sample,
      typeAnalysis: { ...getValues("typeAnalysis"), codigo: analysisTypeCode },
      calculation_analyst: getValues("calculation_analyst"),
      calculation_equipment: getValues("calculation_equipment"),
      calculation_detector: getValues("calculation_detector"),
      counting_date: getValues("calculation_date") || getValues("calculation_date_countig_file") || getValues("counting_date"),
      date_start_sampling: getValues("start_date_time"),
      date_end_sampling: getValues("end_date_time"),
      calculation_amount_sample: getValues("calculation_amount_sample"),
      total_time_counting: getValues("calculation_time_count"),
      geometry: getValues("geometry_height")?.split("-")[0],
      height: getValues("geometry_height")?.split("-")[1],
      wet_dry_weight_ratio: getValues("calculation_wet_dry_weight_ratio"),
      laboratory: getValues("laboratory"),
      unit_types: getValues("calculation_units"),
      preparation_procedure: getValues("calculation_procedure"),
      background: getValues("calculation_background"),
      calibration: getValues("calibration_efficiency_date"),
      library: getValues("calculation_library"),
    };

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <PDFHeader currentDate={currentDate} dataObjects={dataObjects} />
          <PDFTitle maslReference={dataObjects?.masl_reference || ""} analysisDescription={typeAnalysisName || ""} />
          <SampleBasicInfo dataObjects={dataObjects} />
          <PreparationInfo dataObjects={dataObjects} />

          <Text style={{ fontSize: 7.5, fontWeight: "bold", marginTop: 4, marginBottom: 4 }}>{title}</Text>

          <View style={[pdfStyles.tableRow, { backgroundColor: "#f0f0f0", fontWeight: "bold" }]}>
            {columns
              .filter((col) => col.field !== "actions")
              .map((col, index) => (
                <Text key={index} style={index === 0 ? pdfStyles.tableCellLabel : pdfStyles.tableCell}>
                  {col.headerName}
                </Text>
              ))}
          </View>
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={pdfStyles.tableRow}>
                {columns
                  .filter((col) => col.field !== "actions")
                  .map((col, colIndex) => {
                    let cellValue = "";
                    
                    if (col.field === "isotope") {
                      cellValue = row.isotope?.codigo || "";
                    } else if (col.field === "activity") {
                      const activity = parseFloat(row.activity);
                      const lid = parseFloat(row.lid);
                      const uncertainty = parseFloat(row.uncertainty);
                      
                      if (typeReport?.includes("report_characterization") && 
                          typeReport?.includes("only_lid") &&
                          activity <= lid && activity <= uncertainty) {
                        cellValue = "";
                      } else {
                        cellValue = toExponentialUppercaseWhitZero(row.activity, 2);
                      }
                    } else if (col.field === "uncertainty") {
                      const activity = parseFloat(row.activity);
                      const lid = parseFloat(row.lid);
                      const uncertainty = parseFloat(row.uncertainty);
                      
                      if (!row.uncertainty) {
                        cellValue = "";
                      } else if (typeReport?.includes("report_characterization") && 
                          typeReport?.includes("only_lid") &&
                          activity <= lid && activity <= uncertainty) {
                        cellValue = "";
                      } else {
                        cellValue = toExponentialUppercaseWhitZero(row.uncertainty, 2);
                      }
                    } else if (col.field === "lid") {
                      cellValue = toExponentialUppercaseWhitZero(row.lid, 2);
                    } else if (col.field === "lc") {
                      cellValue = toExponentialUppercaseWhitZero(row.lc, 2);
                    } else {
                      cellValue = row[col.field] || "";
                    }

                    return (
                      <Text key={colIndex} style={col.field === "isotope" ? pdfStyles.tableCellLabel : pdfStyles.tableCell}>
                        {cellValue}
                      </Text>
                    );
                  })}
              </View>
            ))}
        </Page>
      </Document>
    );
  };

  const generatePDFDocumentOut1 = () => {
    const currentDate = new Date().toLocaleDateString();
    const sample = get(Number(idMuestra));
    const sourceApp = getValues("source_app_calculation_info") || getValues("source_app_calculation_info_previus");
    const analysisTypeCode = sourceApp === "GENIE" ? "IG-GENIE" : "IG-EXE";
    const title = `Datos de salida 1 - ${typeAnalysisName || ""}`;
    
    const inicio = dayjs(watch("end_date_time")).startOf("day");
    const fin_toma_muestra = dayjs(watch("start_date_time")).startOf("day");
    const fin = dayjs(watch("counting_date")).startOf("day");
    let diffDate = fin.diff(inicio, "minute");
    if (
      sample?.sample_type.codigo == "PP" &&
      ( sample?.accumulation.code === "S" || sample?.accumulation.code === "T" )
    ) {
      const sumTimestamps = inicio.valueOf() + fin_toma_muestra.valueOf();
      const fechaIntermedia = dayjs(sumTimestamps / 2).add(12, 'hour').startOf("day");
      const diffDays = fin.diff(fechaIntermedia, "day", true);
      diffDate = diffDays * 24 * 60;
    }

    const dataObjects = {
      ...sample,
      typeAnalysis: { ...getValues("typeAnalysis"), codigo: analysisTypeCode },
      calculation_analyst: getValues("calculation_analyst"),
      calculation_equipment: getValues("calculation_equipment"),
      calculation_detector: getValues("calculation_detector"),
      counting_date: getValues("counting_date"),
      date_start_sampling: getValues("start_date_time"),
      date_end_sampling: getValues("end_date_time"),
      calculation_amount_sample: getValues("calculation_amount_sample"),
      total_time_counting: getValues("calculation_time_count"),
      geometry: getValues("calculation_equipment"),
      height: getValues("geometry_height"),
      wet_dry_weight_ratio: getValues("calculation_wet_dry_weight_ratio"),
      laboratory: getValues("laboratory"),
      calculation_units: getValues("calculation_units"),
      preparation_procedure: getValues("preparation_procedure"),
      background: getValues("calculation_background"),
      calibration: getValues("calibration_efficiency_date"),
      library: getValues("calculation_library"),
      calculation_tsie_sample: getValues("calculation_tsie_sample"),
      decay_minutes: diffDate,
    };

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={pdfStyles.page}>
          <PDFHeader currentDate={currentDate} dataObjects={dataObjects} />
          <PDFTitle maslReference={dataObjects?.masl_reference || ""} analysisDescription={typeAnalysisName || ""} />
          <SampleBasicInfo dataObjects={dataObjects} />
          <PreparationInfo dataObjects={dataObjects} />

          <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 4 }}>Isótopos con coincidencia</Text>
          <View style={[pdfStyles.tableRow, { backgroundColor: "#f0f0f0", fontWeight: "bold" }]}>
              {columnsWhitMatch.map((col, index) => (
                <Text key={index} style={index === 0 ? pdfStyles.tableCellLabel : pdfStyles.tableCell}>
                  {col.headerName}
                </Text>
              ))}
          </View>
          {rowsInput.map((row, rowIndex) => (
            <View key={rowIndex} style={pdfStyles.tableRow}>
              <Text style={pdfStyles.tableCellLabel}>
                {row.isotope?.codigo || ""}
              </Text>
              <Text style={pdfStyles.tableCell}>
                {toExponentialUppercaseWhitZero(row.activity, 2)}
              </Text>
              <Text style={pdfStyles.tableCell}>
                {toExponentialUppercaseWhitZero(row.uncertainty, 2)}
              </Text>
              <Text style={pdfStyles.tableCell}>
                {toExponentialUppercaseWhitZero(row.lid, 2)}
              </Text>
            </View>
          ))}

          <Text style={{ fontSize: 11, fontWeight: "bold", marginTop: 8, marginBottom: 4 }}>Isótopos solicitados por el cliente</Text>
          <View style={[pdfStyles.tableRow, { backgroundColor: "#f0f0f0", fontWeight: "bold" }]}>
              {columnsWhitOutMatch
                .filter((col) => col.field !== "actions")
                .map((col, index) => (
                  <Text key={index} style={index === 0 ? pdfStyles.tableCellLabel : pdfStyles.tableCell}>
                    {col.headerName}
                  </Text>
                ))}
          </View>
          {memoFilterIsotoposInstalacion.map((row, rowIndex) => (
            <View key={rowIndex} style={pdfStyles.tableRow}>
              <Text style={pdfStyles.tableCellLabel}>
                {row.isotope?.codigo || ""}
              </Text>
              <Text style={pdfStyles.tableCell}>
                {toExponentialUppercaseWhitZero(row.activity, 2)}
              </Text>
              <Text style={pdfStyles.tableCell}>
                {toExponentialUppercaseWhitZero(row.uncertainty, 2)}
              </Text>
              <Text style={pdfStyles.tableCell}>
                {toExponentialUppercaseWhitZero(row.lid, 2)}
              </Text>
            </View>
          ))}
        </Page>
      </Document>
    );
  };

  const handleDownloadPDF = async () => {
    let pdfDoc;
    let title = "";

    if (open.type === "OUT1") {
      pdfDoc = generatePDFDocumentOut1();
      title = `Datos_de_salida_1_${typeAnalysisName || ""}`;
    } else {
      let table: GridColDef[] = [];
      let rows = rowsInput;
      title = `Datos_de_salida_2_${typeAnalysisName || ""}`;

      if (
        typeReport?.includes("report_characterization") &&
        typeReport?.includes("only_lid")
      ) {
        table = columnsOnlyLidORBothTrue;
      } else if (
        !typeReport?.includes("report_characterization") &&
        !typeReport?.includes("only_lid")
      ) {
        table = columnsLidCharterizacionFalse;
      } else if (
        typeReport?.includes("report_characterization") &&
        !typeReport?.includes("only_lid")
      ) {
        table = columnsOnlyCharterizacion();
      } else if (
        !typeReport?.includes("report_characterization") &&
        typeReport?.includes("only_lid")
      ) {
        table = columnsOnlyLidORBothTrue;
      }

      pdfDoc = generatePDFDocument(rows, table, `Datos de salida 2 - ${typeAnalysisName || ""}`);
    }

    const blob = await pdf(pdfDoc).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/\s+/g, "_")}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const renderButton2 = () => {
    let table: GridColDef[] = [];
    if (
      typeReport?.includes("report_characterization") &&
      typeReport?.includes("only_lid")
    ) {
      table = columnsOnlyLidORBothTrue;
    } else if (
      !typeReport?.includes("report_characterization") &&
      !typeReport?.includes("only_lid")
    ) {
      table = columnsLidCharterizacionFalse;
    } else if (
      typeReport?.includes("report_characterization") &&
      !typeReport?.includes("only_lid")
    ) {
      table = columnsOnlyCharterizacion();
    } else if (
      !typeReport?.includes("report_characterization") &&
      typeReport?.includes("only_lid")
    ) {
      table = columnsOnlyLidORBothTrue;
    }
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CustomDataGrid
            key={"data-input-isotopeResultCalculation"}
            rows={rowsInput || []}
            columns={table}
            search={false}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {getValues("idResultPreload") && (
        <>
          <ResponsiveFormContainer columns={1} ml={0.5}>
            <Box display="flex" justifyContent="center" mt={2}>
              <MCheckbox
                key={JSON.stringify(watch("to_report"))}
                name="to_report"
                options={[
                  {
                    label: "Para informe",
                    value: "to_report",
                  },
                ]}
                multiple={false}
                disabled={true}
              />
              <MButton
                variant="contained"
                color="primary"
                text="Utilizar resultado en informe"
                onClick={handleToReportUpdate}
                disabled={getValues("to_report") === "to_report"}
              />
            </Box>
          </ResponsiveFormContainer>
          <Grid
            item
            xs={12}
            md={12}
            display="flex"
            justifyContent="flex-end"
            gap={2}
            mt={2}
          >
            <MButton
              variant="outlined"
              color="secondary"
              text=" Datos de salida 1"
              onClick={() => handleOpen("OUT1")}
            />
            <MButton
              onClick={() => handleOpen("OUT2")}
              text=" Datos de salida 2"
              color="primary"
            />
          </Grid>

          <Modal
            open={open.state}
            onClose={handleClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
          >
            <>
              <Box sx={styleModal} ref={modalContentRef}>
                <Typography
                  id="modal-title-IG"
                  variant="h6"
                  component="h2"
                  sx={{ mb: 2, borderBottom: 3, borderColor: "divider" }}
                >
                  Datos de salida {typeAnalysisName && ` - ${typeAnalysisName}`}
                </Typography>

                {open.type === "OUT1" ? (
                  <>
                    <ResponsiveFormContainer columns={3} my={1}>
                      <MTextField
                        key={JSON.stringify(
                          getValues("calculation_counting_date")
                        )}
                        name="calculation_counting_date"
                        label="Fecha de contaje"
                        type="text"
                        disabled
                        fullWidth
                      />
                      <MAutocomplete
                        key={JSON.stringify(getValues("calculation_equipment"))}
                        name="calculation_equipment"
                        options={equipmentList}
                        label="Equipos y geometrías"
                        getOptionLabel={(option) =>
                          `${option.codigo} - ${
                            option.is_geometry
                              ? (option.height || option.descripcion)
                              : option.descripcion
                          }` || ""
                        }
                        disabled
                        multiple={true}
                      />
                      <MTextField
                        key={JSON.stringify(
                          getValues("calculation_time_count")
                        )}
                        name="calculation_time_count"
                        label="Tiempo total contaje (min)"
                        type="text"
                        disabled
                        fullWidth
                      />
                    </ResponsiveFormContainer>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <CustomDataGrid
                          key={"data-input-isotopeResultCalculation"}
                          rows={rowsInput || []}
                          columns={columnsWhitMatch}
                          search={false}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <CustomDataGrid
                            key={"data-input-isotopeResultCalculation"}
                            rows={memoFilterIsotoposInstalacion}
                            columns={columnsWhitOutMatch || []}
                            search={false}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  renderButton2()
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 2,
                    gap: 2,
                  }}
                >
                  <MButton
                    onClick={handleDownloadPDF}
                    text="Descargar PDF"
                    variant="outlined"
                    color="primary"
                  />
                  <MButton
                    onClick={handleClose}
                    text="Cerrar"
                    variant="contained"
                  />
                </Box>
              </Box>
            </>
          </Modal>
        </>
      )}
    </>
  );
};

export default OutData;
